import './index.scss';

import plusIcon from 'url:@assets/icons/plusNoCircle.svg';
import volunteerIcon from 'url:@assets/icons/volunteer-icon.svg';

import ErrorView from '@components/ErrorView';
import LoadingIndicator from '@components/LoadingIndicator';
import useVolunteerTeams from '@hooks/Volunteering/useVolunteerTeams';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateTeamModal from './CreateTeamModal';
import TeamsWorkspace, { teamPagePath } from './TeamsWorkspace';

// Every team, each previewing a few volunteers. A team's own page has the full,
// searchable list, so a big team doesn't bury the ones below it here.
export default function VolunteeringTeamsPage() {
  const navigate = useNavigate();
  const { teams, loading, error, refetch } = useVolunteerTeams();

  // Only the very first load blanks the page. A later refetch -- after an
  // invite, a removal, or a token refresh -- leaves what's on screen alone so
  // an open modal keeps its state.
  const isFirstLoad = loading && !teams;
  const bundles = teams?.results ?? [];

  // Keyed per open, so every open starts back at the source step.
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(0);

  const openCreateTeam = () => {
    setCreateKey((currentKey) => currentKey + 1);
    setIsCreateOpen(true);
  };

  const createTeamModal = <CreateTeamModal
    key={`create-team-${createKey}`}
    isOpen={isCreateOpen}
    close={() => setIsCreateOpen(false)}
    onCreated={(team, name) => {
      setIsCreateOpen(false);
      // The new team is empty, so its page -- where volunteers get invited --
      // is the next place to be. The name rides along for the header.
      navigate(teamPagePath(team.id), { state: { teamName: name } });
    }} />;

  if (!loading && !error && bundles.length === 0) {
    return <>
      {createTeamModal}

      <ErrorView
        icon={volunteerIcon}
        title='No Teams Yet'
        error='Create a volunteer team to get started.'
        buttonText='New Team'
        buttonAction={openCreateTeam}
        fullHeight />
    </>
  }

  return <div className='volunteering-people-page'>
    {createTeamModal}

    {isFirstLoad ? <LoadingIndicator fullScreen={true} /> : null}

    {!isFirstLoad && <div className='teams-page-header'>
      <h2>Teams</h2>

      <button type='button' className='new-team-button' onClick={openCreateTeam}>
        <img src={plusIcon} alt='' aria-hidden='true' />
        New Team
      </button>
    </div>}

    <TeamsWorkspace bundles={bundles} refetch={refetch} variant='overview' />

    {error && <p className='error'>{error}</p>}
  </div>
}
